import {
  cloneElement,
  type CSSProperties,
  isValidElement,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";
import { type SwitchThumbProps } from "@radix-ui/react-switch";

export type DraggableXProps = {
  children?: ReactElement<SwitchThumbProps>;
  disabled?: boolean;
  position: number;
  bounds: { left: number; right: number };
  onStart?: () => void;
  onDrag?: (args: { x: number }) => void;
  onStop?: () => void;
  style?: CSSProperties;
};

const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent => e && "touches" in e;

/**
 * Bare minimum to make the toggle draggable.
 * It is created for usage in the toggle only but could easily be extracted and used elsewhere.
 *
 * It is called `DraggableX` since it's only for dragging along the x-axis.
 */
export const DraggableX = ({
  children,
  disabled,
  onStart,
  onDrag,
  onStop,
  bounds,
  position,
  style,
}: DraggableXProps) => {
  const [dragX, setDragX] = useState<number | undefined>(undefined);
  const [dragStartX, setDragStartX] = useState<number | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);

  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!disabled) {
      e.preventDefault();
      const clientX = isTouchEvent(e.nativeEvent)
        ? e.nativeEvent.touches[0].clientX
        : e.nativeEvent.clientX;
      setDragStartX(clientX);
      onStart?.();
    }
  };

  const onMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (typeof dragStartX !== "number") {
        return;
      }

      const clientX = isTouchEvent(e) ? e.touches[0].clientX : e.clientX;
      const dragDelta = clientX - dragStartX;

      if (!dragDelta) {
        return;
      }

      const nextDragX = position + dragDelta;
      if (!isDragging && Math.abs(nextDragX)) {
        setIsDragging(true);
      }

      const nextDragXBounded = (() => {
        if (nextDragX > bounds.right) {
          return bounds.right;
        }

        if (nextDragX < bounds.left) {
          return bounds.left;
        }

        return nextDragX;
      })();

      setDragX(nextDragXBounded);
      onDrag?.({ x: nextDragXBounded });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDrag, setDragX, isDragging, dragStartX]
  );

  const onMouseUp = useCallback(() => {
    setDragStartX(undefined);
    setDragX(undefined);
    setIsDragging(false);
    onStop?.();
  }, [onStop, setDragStartX]);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("touchmove", onMouseMove);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onMouseMove);
    };
  }, [onMouseMove]);

  useEffect(() => {
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchend", onMouseUp);

    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchend", onMouseUp);
    };
  }, [onMouseUp]);

  return isValidElement(children)
    ? cloneElement<SwitchThumbProps>(children, {
        onMouseDown: startDrag,
        onTouchStart: startDrag,
        style: { ...style, transform: `translateX(${isDragging ? dragX : position}px)` },
      })
    : null;
};
